import React, { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import withRouter from "components/Common/withRouter";

import { Button, Card, CardBody, Col, Container } from "reactstrap";

import TableContainer from "components/Common/TableContainer";
import {
  submitAIScheduleMatrixFilterData,
  deleteAIScheduleMatrix,
} from "model/AIScheduleMatrixModel";
import { useAIScheduleMatrixContext } from "libs/context/AIScheduleMatrixContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import AIScheduleMatrixAddEditModal from "pages/AllCommonModal/AIScheduleMatrixAddEditModal/ai-schedule-matrix-add-edit-modal";
import Loaders from "components/Common/Loaders";
import { toast } from "react-toastify";

const PAGE_NAME = "AIScheduleMatrix";

/** Filter API returns `{ data: [...] }` — plain array, no pagination envelope. */
const extractList = (response) => {
  if (response == null) return [];
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.records)) return raw.records;
  if (raw && Array.isArray(raw.data)) return raw.data;
  return [];
};

const mapRows = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    ...item,
    id: item?.id ?? item?.Id ?? item?._id,
  }));

const AIScheduleMatrix = () => {
  const {
    fetchAIScheduleMatrixContextData,
    aiScheduleMatrixContextData,
  } = useAIScheduleMatrixContext() || {};
  const { permissionData } = usePermissionContext() || {};

  const actionPermissions = useMemo(
    () => ({
      canCreate: Boolean(
        permissionData?.AIScheduleMatrixAddAccess
      ),
      canEdit: Boolean(permissionData?.AIScheduleMatrixUpdateAccess),
      canDelete: Boolean(permissionData?.AIScheduleMatrixDeleteAccess),
    }),
    [permissionData]
  );

  const hasAnyRowAction = useMemo(
    () =>
      Boolean(actionPermissions.canEdit || actionPermissions.canDelete),
    [actionPermissions]
  );

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [editRow, setEditRow] = useState(null);

  const tableMetaData = useMemo(
    () =>
      aiScheduleMatrixContextData?.table?.columns?.map((col) => ({
        Header: col?.title,
        accessor: col?.field,
        type: col?.type,
      })),
    [aiScheduleMatrixContextData]
  );

  const getList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await submitAIScheduleMatrixFilterData({});
      const list = extractList(response);
      setTableData(mapRows(Array.isArray(list) ? list : []));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAIScheduleMatrixContextData?.();
    getList();
  }, []);

  const handleAdd = () => {
    setEditItemId(null);
    setEditRow(null);
    setEditModalOpen(true);
  };

  const handleEdit = (row) => {
    const recordId = row?.id ?? row?.Id ?? row?._id;
    if (!recordId) {
      toast.error("Record id is missing");
      return;
    }
    setEditItemId(recordId);
    setEditRow(row && typeof row === "object" ? { ...row } : {});
    setEditModalOpen(true);
  };

  const handleDelete = async (row) => {
    const recordId = String(row?.id ?? row?.Id ?? row?._id ?? "").trim();
    if (!recordId) {
      toast.error("Record id is missing");
      return;
    }

    const result = await Swal.fire({
      title: "Delete schedule matrix",
      text: "Are you sure you want to delete this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const response = await deleteAIScheduleMatrix(recordId);
      if (response?.success === false) {
        toast.error(response?.message || "Unable to delete");
        return;
      }
      toast.success(response?.message || "Deleted successfully");
      await getList();
    } finally {
      setLoading(false);
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditItemId(null);
    setEditRow(null);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <h5 className="fw-bold mb-0">AI Schedule Matrix</h5>
                {actionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0 px-0">
                    <Button color="primary" onClick={handleAdd}>
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              {!loading && tableMetaData?.length ? (
                <TableContainer
                  columns={tableMetaData}
                  data={tableData}
                  isActionRequired={hasAnyRowAction}
                  isEditAccess={actionPermissions.canEdit}
                  isDeleteAccess={actionPermissions.canDelete}
                  onEdit={
                    actionPermissions.canEdit ? handleEdit : undefined
                  }
                  onDelete={
                    actionPermissions.canDelete ? handleDelete : undefined
                  }
                  isCheckboxLeftSide={false}
                  isPagination={false}
                  pageName={PAGE_NAME}
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>

          <AIScheduleMatrixAddEditModal
            isOpen={editModalOpen}
            onClose={closeEditModal}
            itemId={editItemId}
            editableRow={editRow}
            onSuccess={getList}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

AIScheduleMatrix.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(AIScheduleMatrix);
